import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import clsx from "clsx";

import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { Text, View, DropdownField, Card, TabPanel, Spinner } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { EmptyState } from "../../components/base/EmptyState";
import { Release, releaseActions } from "../../redux/reducers/releaseSlice";
import { environmentActions } from "../../redux/reducers/environmentSlice";
import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { userActions } from "../../redux/reducers/userSlice";
import { Entrypoint, EntrypointType, entrypointActions } from "../../redux/reducers/entrypointSlice";
import { capitalize } from "../../utils/strings";
import { useCoachOverlay } from "../../components/onboarding/useCoachOverlay";
import { releasesOverlay } from "../../components/onboarding/coachOverlays";

const STATUS_STYLES: Record<Release["status"], string> = {
  released: "bg-success/10 dark:bg-success/20 text-success-dark dark:text-success-light",
  pending: "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300",
  failed: "bg-error/10 dark:bg-error/20 text-error-dark dark:text-error-light",
};

const ENTRYPOINT_TYPE_ICON: Record<EntrypointType, string> = {
  phone: "phone",
  web: "language",
  native: "code",
};

const summarizeEntrypointConfig = (entrypoint: Entrypoint): string | null => {
  if (entrypoint.type === "phone") {
    const config = entrypoint.config as { phoneNumber?: string; friendlyName?: string };
    return config.friendlyName || config.phoneNumber || null;
  }
  if (entrypoint.type === "web") {
    const config = entrypoint.config as { allowedOrigins?: string[] };
    const origins = config.allowedOrigins ?? [];
    if (origins.length === 0) return null;
    return origins.length === 1 ? origins[0] : `${origins[0]} (+${origins.length - 1} more)`;
  }
  if (entrypoint.type === "native") {
    const config = entrypoint.config as { clientId?: string };
    return config.clientId || null;
  }
  return null;
};

export const AgentReleasesPage = () => {
  const dispatch = useAppDispatch();
  const { agentId } = useParams<{ agentId: string }>();
  const { organizationId } = useEffectiveOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEnvironmentId = searchParams.get("e") || "";

  const releaseEntities = useAppSelector((state) => state.release.entities);
  const environmentEntities = useAppSelector((state) => state.environment.entities);
  const agentFunctionEntities = useAppSelector((state) => state.agentFunction.entities);
  const userEntities = useAppSelector((state) => state.user.entities);
  const entrypointEntities = useAppSelector((state) => state.entrypoint.entities);
  const isLoading = useAppSelector((state) => state.release.isLoading);

  useCoachOverlay(releasesOverlay);

  useEffect(() => {
    if (!agentId || !organizationId) return;
    dispatch(releaseActions.get({ agentId }));
    dispatch(environmentActions.get({}));
    dispatch(agentFunctionActions.get({ agentId }));
    dispatch(userActions.get({}));
    dispatch(entrypointActions.get({}));
  }, [agentId, organizationId, dispatch]);

  const releases = useMemo(() => {
    return Object.values(releaseEntities)
      .filter((release) => release.agentId === agentId && (selectedEnvironmentId ? release.environmentId === selectedEnvironmentId : true))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [releaseEntities, agentId, selectedEnvironmentId]);

  const referencedEnvironments = useMemo(() => {
    const ids = new Set(
      Object.values(releaseEntities)
        .filter((r) => r.agentId === agentId)
        .map((r) => r.environmentId),
    );
    return Array.from(ids)
      .map((id) => environmentEntities[id])
      .filter(Boolean);
  }, [releaseEntities, environmentEntities, agentId]);

  const agentReleaseIds = useMemo(
    () =>
      new Set(
        Object.values(releaseEntities)
          .filter((r) => r.agentId === agentId)
          .map((r) => r.id),
      ),
    [releaseEntities, agentId],
  );

  const matchingEntrypoints = useMemo(() => {
    return Object.values(entrypointEntities)
      .filter((entrypoint) => entrypoint.releases.some((wr) => agentReleaseIds.has(wr.releaseId)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entrypointEntities, agentReleaseIds]);

  const getEnvironmentName = (id: string) => environmentEntities[id]?.name || id;
  const getFunctionName = (id?: string) => {
    if (!id) return "—";
    const fn = agentFunctionEntities[id];
    return fn?.displayName || fn?.name || id;
  };
  const getUserLabel = (id: string) => userEntities[id]?.email || userEntities[id]?.name || id;

  if (isLoading && releases.length === 0) {
    return (
      <View className="flex flex-col items-center justify-center py-12">
        <Spinner size={32} />
        <Text className="mt-4 text-gray-500">Loading releases...</Text>
      </View>
    );
  }

  return (
    <TabPanel>
      {releases.length > 0 && (
        <View className="space-y-2">
          <View className="flex flex-row items-center justify-between">
            <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Entrypoints</Text>
            {matchingEntrypoints.length > 0 && (
              <Text className="text-xs text-gray-400 dark:text-gray-500">{matchingEntrypoints.length} routing to this agent</Text>
            )}
          </View>
          {matchingEntrypoints.length === 0 ? (
            <Card className="p-4 border-dashed bg-transparent">
              <View className="flex flex-row items-center gap-3">
                <View className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 shrink-0">
                  <Icon
                    name="link"
                    size={14}
                    className="text-gray-400 dark:text-gray-500"
                  />
                </View>
                <View className="flex flex-col min-w-0">
                  <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">No entrypoints route to this agent</Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">
                    Entrypoints (phone numbers, web widgets, native clients) need to point at one of the releases below to receive traffic.
                  </Text>
                </View>
              </View>
            </Card>
          ) : (
            <View className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {matchingEntrypoints.map((entrypoint) => {
                const totalWeight = entrypoint.releases.reduce((acc, wr) => acc + (wr.weight || 0), 0) || 1;
                const agentSlices = entrypoint.releases.filter((wr) => agentReleaseIds.has(wr.releaseId));
                const agentPercent = Math.round((agentSlices.reduce((acc, wr) => acc + wr.weight, 0) / totalWeight) * 100);
                const isActive = entrypoint.status === "active";
                const configSummary = summarizeEntrypointConfig(entrypoint);
                return (
                  <Card
                    key={entrypoint.id}
                    className="p-4 transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600"
                  >
                    <View className="flex flex-row items-center justify-between gap-2 mb-2">
                      <View className="flex flex-row items-center gap-2 min-w-0">
                        <Icon
                          name={ENTRYPOINT_TYPE_ICON[entrypoint.type]}
                          size={16}
                          className="text-gray-500 dark:text-gray-400 shrink-0"
                        />
                        <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{entrypoint.name}</Text>
                      </View>
                      <View
                        className={clsx(
                          "px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0",
                          isActive
                            ? "bg-success/10 dark:bg-success/20 text-success-dark dark:text-success-light"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400",
                        )}
                      >
                        {capitalize(entrypoint.status)}
                      </View>
                    </View>
                    <View className="flex flex-row items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                      <Text className="capitalize">{entrypoint.type}</Text>
                      {configSummary && (
                        <>
                          <Text>·</Text>
                          <Text className="truncate">{configSummary}</Text>
                        </>
                      )}
                    </View>
                    <View className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                      <View className="flex flex-row items-center justify-between text-[11px]">
                        <Text className="text-gray-500 dark:text-gray-400">Traffic to this agent</Text>
                        <Text className="font-semibold text-gray-900 dark:text-white">{agentPercent}%</Text>
                      </View>
                      {agentSlices.map((wr) => {
                        const release = releaseEntities[wr.releaseId];
                        const envName = release ? getEnvironmentName(release.environmentId) : wr.releaseId;
                        const functionName = release ? getFunctionName(release.functionId) : null;
                        const slicePercent = Math.round((wr.weight / totalWeight) * 100);
                        const isKnownRelease = Boolean(release);
                        return (
                          <a
                            key={wr.releaseId}
                            href={isKnownRelease ? `#release-${wr.releaseId}` : undefined}
                            className={clsx(
                              "flex flex-row items-start justify-between gap-2 text-[11px] -mx-1 px-1 py-0.5 rounded",
                              isKnownRelease && "hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors",
                            )}
                          >
                            <View className="flex flex-row items-start gap-1.5 min-w-0">
                              <Icon
                                name="arrow-right"
                                size={10}
                                className="text-gray-400 shrink-0 mt-0.5"
                              />
                              <View className="flex flex-col min-w-0">
                                <Text className={clsx("truncate", isKnownRelease ? "text-brand dark:text-brand-light" : "text-gray-700 dark:text-gray-300")}>
                                  {envName}
                                </Text>
                                {functionName && functionName !== "—" && <Text className="text-gray-400 dark:text-gray-500 truncate">{functionName}</Text>}
                              </View>
                            </View>
                            <Text className="text-gray-500 dark:text-gray-400 shrink-0">{slicePercent}%</Text>
                          </a>
                        );
                      })}
                    </View>
                  </Card>
                );
              })}
            </View>
          )}
        </View>
      )}

      {referencedEnvironments.length > 1 && (
        <View className="flex justify-end">
          <View
            className="w-auto"
            data-onboarding="env-filter"
          >
            <DropdownField
              name="environment"
              placeholder="All environments"
              value={selectedEnvironmentId}
              onChange={(value: string) => {
                const next = new URLSearchParams(searchParams);
                if (value) next.set("e", value);
                else next.delete("e");
                setSearchParams(next);
              }}
              options={[{ label: "All environments", value: "" }, ...referencedEnvironments.map((env) => ({ label: env.name, value: env.id }))]}
            />
          </View>
        </View>
      )}

      {releases.length === 0 ? (
        <EmptyState
          icon="rocket_launch"
          title="No releases yet"
          description="Releases will appear here when this agent is released to an environment."
        />
      ) : (
        <View
          className="border border-gray-200 dark:border-gray-700 rounded-md"
          data-onboarding="release-list"
        >
          {releases.map((release, index) => (
            <View
              key={release.id}
              id={`release-${release.id}`}
              className={clsx(
                "py-3 px-4 bg-white dark:bg-gray-800 scroll-mt-4 [&:target]:bg-brand/5 dark:[&:target]:bg-brand-light/10",
                index !== 0 && "border-t border-gray-200 dark:border-gray-700",
              )}
            >
              <View className="flex flex-row items-center gap-2 mb-1">
                <Text className="text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate">{release.id}</Text>
              </View>
              <View className="flex flex-row justify-between items-start gap-4">
                <View className="flex flex-col flex-1 min-w-0">
                  <View className="flex flex-row items-center gap-2 mb-2 flex-wrap">
                    <Text className="font-medium text-sm">{getFunctionName(release.functionId)}</Text>
                    <View className={clsx("px-2 py-0.5 rounded-full text-xs font-medium", STATUS_STYLES[release.status])}>{capitalize(release.status)}</View>
                  </View>
                  <View className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <Text>
                      <span className="font-medium">Environment:</span>{" "}
                      <span className="text-gray-900 dark:text-white">{getEnvironmentName(release.environmentId)}</span>
                    </Text>
                    <Text>
                      <span className="font-medium">Created:</span>{" "}
                      <span className="text-gray-900 dark:text-white">{new Date(release.createdAt).toLocaleString()}</span>
                    </Text>
                    <Text>
                      <span className="font-medium">By:</span> <span className="text-gray-900 dark:text-white">{getUserLabel(release.userId)}</span>
                    </Text>
                    {release.runtime?.primfunctionsVersion && (
                      <Text>
                        <span className="font-medium">primfunctions:</span>{" "}
                        <span className="font-mono text-gray-900 dark:text-white">{release.runtime.primfunctionsVersion}</span>
                      </Text>
                    )}
                  </View>
                  {release.runtime?.image && (
                    // Only present for global admins — the API strips runtime.image
                    // for org users. Purple per the admin-feature convention.
                    <Text className="mt-1 text-[11px] font-mono text-purple-600 dark:text-purple-400 truncate">
                      <span title={release.runtime.image}>{release.runtime.image}</span>
                    </Text>
                  )}
                </View>
              </View>
            </View>
          ))}
        </View>
      )}
    </TabPanel>
  );
};
